import React, { useEffect, useRef } from 'react';

/**
 * McaModal — Accessible modal dialog with scrim, close button, and keyboard support.
 * 
 * Props:
 *   - open (bool): Control modal visibility
 *   - onClose (fn): Called when modal closes (scrim click, Esc, data-close button)
 *   - title (string): Modal title (h3)
 *   - children (node): Modal body content
 *   - footer (node): Optional footer slot (buttons, etc.)
 *   - static (bool): If true, scrim click doesn't close (e.g., confirmation dialogs)
 *   - ariaLabelledBy (string): aria-labelledby (auto-set from title if provided)
 */
export function McaModal({ open, onClose, title, children, footer, static: isStatic, ariaLabelledBy }) {
  const overlayRef = useRef(null);
  const headRef = useRef(null);
  const titleId = useRef(null);

  // Auto-generate title ID if title prop exists
  useEffect(() => {
    if (title && !ariaLabelledBy) {
      titleId.current = 'mca-modal-title-' + Math.random().toString(36).slice(2);
    }
  }, [title, ariaLabelledBy]);

  // Handle open/close class and focus management
  useEffect(() => {
    const ov = overlayRef.current;
    if (!ov) return;

    if (open) {
      ov.classList.add('is-open');
      ov.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Focus first focusable element after short delay for animation
      setTimeout(() => {
        const focusable = ov.querySelector('input, button, select, textarea, [tabindex="0"]');
        if (focusable) focusable.focus();
      }, 60);
    } else {
      ov.classList.remove('is-open');
      ov.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }, [open]);

  const handleEscape = (e) => {
    if (e.key === 'Escape' && open) {
      onClose?.();
    }
  };

  const handleScrimClick = (e) => {
    if (e.target === e.currentTarget && !isStatic) {
      onClose?.();
    }
  };

  const handleCloseClick = () => {
    onClose?.();
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, onClose]);

  return (
    <div
      ref={overlayRef}
      data-overlay
      aria-hidden={!open}
      className="mca-overlay"
      onClick={handleScrimClick}
    >
      <div className="mca-scrim" data-scrim />
      <div
        className="mca-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy || titleId.current}
      >
        {/* HEAD: title + close button */}
        {(title || children) && (
          <div className="mca-modal__head" ref={headRef}>
            <div>
              {title && (
                <h3 id={titleId.current}>{title}</h3>
              )}
            </div>
            <button
              className="mca-overlay-close"
              onClick={handleCloseClick}
              aria-label="Close"
              type="button"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* BODY: main content */}
        {children && (
          <div className="mca-modal__body">
            {children}
          </div>
        )}

        {/* FOOT: action buttons */}
        {footer && (
          <div className="mca-modal__foot">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default McaModal;
